import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQueryClient } from '@tanstack/react-query'
import { X, Check, Trash2 } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { track } from '../../lib/posthog'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ExpiryItem {
  id:                      string
  name:                    string
  emoji:                   string
  household_id:            string
  current_expiration_date: string | null
  total_expired_count?:    number | null
  total_used_count?:       number | null
  total_wasted_dollars?:   number | null
}

interface Props {
  item:       ExpiryItem
  onClose:    () => void
  onSuccess?: (message: string) => void
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Find the soonest-expiring active batch and mark it 'used'. The Phase 4
// trigger then recomputes pantry_items.current_quantity and
// current_expiration_date from the remaining active batches — so we never
// write current_expiration_date directly from the sheet anymore.
async function consumeSoonestBatch(itemId: string): Promise<void> {
  const { data: batch } = await supabase
    .from('item_batches')
    .select('id')
    .eq('item_id', itemId)
    .eq('status', 'active')
    .order('expiry_date', { ascending: true, nullsFirst: false })
    .order('created_at',  { ascending: true })
    .limit(1)
    .maybeSingle()

  if (!batch) {
    // Legacy item with no active batches — nothing to consume. The trigger
    // won't fire, current_expiration_date stays at its existing value.
    // Acceptable: the user only gets here when an item HAD an expiry, and
    // by getting here without batches it's likely already null anyway.
    console.warn('[ConfirmExpirySheet] no active batch to consume for item', itemId)
    return
  }

  const { error } = await supabase
    .from('item_batches')
    .update({ status: 'used', quantity: 0 })
    .eq('id', batch.id)
  if (error) throw error
}

// ─── ConfirmExpirySheet ───────────────────────────────────────────────────────

export default function ConfirmExpirySheet({ item, onClose, onSuccess }: Props) {
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(false)

  const expiryDateLabel = item.current_expiration_date
    ? new Date(item.current_expiration_date + 'T00:00:00').toLocaleDateString('en-US', {
        month: 'short', day: 'numeric',
      })
    : '—'

  function showToast(message: string) {
    onSuccess?.(message)
  }

  // ── Wasted handler ────────────────────────────────────────────────────────

  async function handleWasted() {
    setLoading(true)
    try {
      // Pull most-recent paid price as the wasted-dollar estimate.
      const { data: recentConfirmation } = await supabase
        .from('confirmations')
        .select('price_paid')
        .eq('item_id', item.id)
        .not('price_paid', 'is', null)
        .order('confirmed_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      const pricePaid = recentConfirmation?.price_paid ?? 0

      // Update the existing unconfirmed expiration_event or insert a new one.
      const { data: existingEvent } = await supabase
        .from('expiration_events')
        .select('id')
        .eq('item_id', item.id)
        .is('confirmed_at', null)
        .maybeSingle()

      if (existingEvent) {
        await supabase
          .from('expiration_events')
          .update({
            used_before_expiry: false,
            expired:            true,
            confirmed_at:       new Date().toISOString(),
            wasted_dollars:     pricePaid,
            price_paid:         pricePaid,
          })
          .eq('id', existingEvent.id)
      } else {
        await supabase
          .from('expiration_events')
          .insert({
            household_id:       item.household_id,
            item_id:            item.id,
            expiration_date:    item.current_expiration_date,
            used_before_expiry: false,
            expired:            true,
            confirmed_at:       new Date().toISOString(),
            wasted_dollars:     pricePaid,
            price_paid:         pricePaid,
          })
      }

      // Bump aggregate counters. NOTE: do NOT write current_expiration_date
      // directly — the soonest batch's status='used' below triggers the
      // Phase 4 recompute which clears it (or shifts it to the next batch).
      await supabase
        .from('pantry_items')
        .update({
          total_expired_count:  (item.total_expired_count  ?? 0) + 1,
          total_wasted_dollars: (item.total_wasted_dollars ?? 0) + pricePaid,
        })
        .eq('id', item.id)

      // Consume the soonest batch — trigger updates current_quantity and
      // current_expiration_date from the remaining active batches.
      await consumeSoonestBatch(item.id)

      showToast(
        pricePaid > 0
          ? `$${pricePaid.toFixed(2)} tracked as waste`
          : 'Marked as wasted — add a price next time for exact tracking'
      )

      queryClient.invalidateQueries({ queryKey: ['expiration-waste'] })
      queryClient.invalidateQueries({ queryKey: ['pantry-items'] })
      queryClient.invalidateQueries({ queryKey: ['item-batches'] })
      track('item_consumed', { type: 'wasted' })
      onClose()
    } catch (err) {
      console.error('[ConfirmExpirySheet] waste confirmation failed:', err)
    } finally {
      setLoading(false)
    }
  }

  // ── Used handler ──────────────────────────────────────────────────────────

  async function handleUsed() {
    setLoading(true)
    try {
      const { data: existingEvent } = await supabase
        .from('expiration_events')
        .select('id')
        .eq('item_id', item.id)
        .is('confirmed_at', null)
        .maybeSingle()

      if (existingEvent) {
        await supabase
          .from('expiration_events')
          .update({
            used_before_expiry: true,
            expired:            false,
            confirmed_at:       new Date().toISOString(),
            wasted_dollars:     0,
          })
          .eq('id', existingEvent.id)
      } else {
        await supabase
          .from('expiration_events')
          .insert({
            household_id:       item.household_id,
            item_id:            item.id,
            expiration_date:    item.current_expiration_date,
            used_before_expiry: true,
            expired:            false,
            confirmed_at:       new Date().toISOString(),
            wasted_dollars:     0,
          })
      }

      await supabase
        .from('pantry_items')
        .update({
          total_used_count: (item.total_used_count ?? 0) + 1,
        })
        .eq('id', item.id)

      await consumeSoonestBatch(item.id)

      showToast('Great — no waste on this one')

      queryClient.invalidateQueries({ queryKey: ['expiration-waste'] })
      queryClient.invalidateQueries({ queryKey: ['pantry-items'] })
      queryClient.invalidateQueries({ queryKey: ['item-batches'] })
      track('item_consumed', { type: 'used' })
      onClose()
    } catch (err) {
      console.error('[ConfirmExpirySheet] used confirmation failed:', err)
    } finally {
      setLoading(false)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex flex-col justify-end items-center">

        <motion.div
          className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />

        <motion.div
          className="relative bg-cream rounded-t-[28px] shadow-2xl w-full max-w-[480px] pb-8"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="h-1 w-10 rounded-full bg-border-avatar" />
          </div>

          {/* Masthead */}
          <div className="flex items-start justify-between px-5 pt-2 pb-4 gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-terracotta mb-1">
                Past expiry
              </div>
              <h2 className="font-serif text-[22px] font-normal leading-[1.15] tracking-[-0.015em] text-ink">
                Did you finish the <em className="italic">{item.name}</em>?
              </h2>
              <p className="font-serif text-[13px] italic text-ink-muted mt-2 leading-[1.45]">
                Expired on {expiryDateLabel}.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex-shrink-0 h-8 w-8 rounded-[10px] border border-border-sand bg-paper flex items-center justify-center text-ink-muted"
            >
              <X className="h-[14px] w-[14px]" strokeWidth={1.8} />
            </button>
          </div>

          {/* Choices */}
          <div className="px-5 flex flex-col gap-2">
            <button
              type="button"
              onClick={handleUsed}
              disabled={loading}
              className="w-full rounded-[14px] border border-forest/40 bg-ok-bg/60 px-4 py-3.5 flex items-center gap-3 text-left disabled:opacity-50 transition-colors active:bg-ok-bg"
            >
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-forest text-forest-ink">
                <Check className="h-4 w-4" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-[15px] font-medium text-ink leading-tight">
                  Yes, used it all
                </p>
                <p className="font-serif text-[12px] italic text-forest mt-0.5 leading-[1.4]">
                  No waste — great job.
                </p>
              </div>
            </button>

            <button
              type="button"
              onClick={handleWasted}
              disabled={loading}
              className="w-full rounded-[14px] border border-border-sand bg-paper px-4 py-3.5 flex items-center gap-3 text-left disabled:opacity-50 transition-colors active:bg-cream-warm"
            >
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-cream-warm">
                <Trash2 className="h-4 w-4 text-terracotta" strokeWidth={1.8} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-[15px] font-medium text-ink leading-tight">
                  No, it went to waste
                </p>
                <p className="font-serif text-[12px] italic text-ink-muted mt-0.5 leading-[1.4]">
                  We'll log the cost so your insights stay accurate.
                </p>
              </div>
            </button>

            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="w-full py-3 font-serif text-[12px] italic text-ink-muted"
            >
              Skip for now
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}
